import React, { useState, useMemo, useEffect } from "react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { ArrowLeft } from "lucide-react";
import { LoadingOverlay } from "../LoadingOverlay";
import { Toaster, toast } from "sonner";

import { PersonalDataForm } from "../forms/PersonalDataForm";
import { AddressForm } from "../forms/AddressForm";
import { BankDataForm } from "../forms/BankDataForm";
import { ProcessList } from "../ProcessList";
import { UserFormSubmitSection } from "./UserFormSubmitSection";
import { isPixKeyValid } from "./userFormValidation";
import { personalDataSchema, addressSchema, bankDataSchema } from "../../schemas/userSchemas";
import { isValidEmail, isValidPhoneBR, isValidCPF } from "../../lib/utils";

export function UserFormView({
  userData,
  bancoData,
  selectedProcessIds,
  setSelectedProcessIds,
  processTypes,
  toggleProcessType,
  isSubmitting,
  handleUserDataChange,
  handleBancoChange,
  handleSubmit,
  isAdminMode,
  onReturnToAdmin,
  onSubmitSuccess,
  setCessao,
  submitCessao,
  initializeProcesses,
}) {
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFalecido, setIsFalecido] = useState(false);

  useEffect(() => {
    if (userData) {
      initializeProcesses();
    }
  }, [userData, initializeProcesses]);

  const filteredProcesses = useMemo(() => {
    if (!userData?.processos) return [];
    return userData.processos.filter(p => p.tipo_aceite !== 'cessao');
  }, [userData]);

  const cessaoProcesses = useMemo(() => {
    if (!userData?.processos) return [];
    return userData.processos.filter(p => p.tipo_aceite === 'cessao');
  }, [userData]);

  const pixKeyInvalid = !isPixKeyValid(bancoData, isValidCPF, isValidEmail, isValidPhoneBR);

  const handleFormSubmit = async (tipoAceite, onSuccess) => {
    // Zod Validation
    const personalResult = personalDataSchema.safeParse(userData);
    const addressResult = addressSchema.safeParse(userData);
    const bankResult = bankDataSchema.safeParse(bancoData);

    const newErrors = {};
    if (!personalResult.success) {
      personalResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }
    if (!addressResult.success) {
      addressResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }
    if (!bankResult.success) {
      bankResult.error.issues.forEach(issue => newErrors[issue.path[0]] = issue.message);
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Por favor, preencha todos os campos obrigatórios destacados.");
      return;
    }

    setErrors({});

    const success = await handleSubmit(tipoAceite, isFalecido);

    if (success) {
      if (onSuccess) {
        onSuccess();
      } else {
        onSubmitSuccess();
        setIsModalOpen(false);
      }
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-4 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 w-full overflow-y-auto">
      <Toaster richColors />

      {isSubmitting && (
        <LoadingOverlay message="Estamos gerando seus contratos, isso pode demorar alguns segundos." />
      )}

      <div className="max-w-5xl mx-auto pb-8">
        {isAdminMode && (
          <Button
            variant="ghost"
            onClick={onReturnToAdmin}
            className="mb-6 gap-2 hover:bg-white/80 transition-all shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Painel de Admin
          </Button>
        )}

        <Card className="p-4 sm:p-8 md:p-10 shadow-float border-0 bg-white/95 backdrop-blur-sm overflow-visible">
          {userData ? (
            <>
              <PersonalDataForm
                userData={userData}
                handleUserDataChange={handleUserDataChange}
                errors={errors}
                isAdminMode={isAdminMode}
                setIsFalecido={setIsFalecido}
              />

              <Separator className="my-6 md:my-10 bg-gradient-to-r from-transparent via-slate-300 to-transparent h-px" />

              <AddressForm
                userData={userData}
                handleUserDataChange={handleUserDataChange}
                errors={errors}
              />

              <Separator className="my-6 md:my-10 bg-gradient-to-r from-transparent via-slate-300 to-transparent h-px" />

              <BankDataForm
                bancoData={bancoData}
                handleBancoChange={handleBancoChange}
                errors={errors}
                pixKeyInvalid={pixKeyInvalid}
              />

              <Separator className="my-6 md:my-10 bg-gradient-to-r from-transparent via-slate-300 to-transparent h-px" />

              {filteredProcesses.length > 0 && (
                <>
                  <ProcessList
                    processes={filteredProcesses}
                    selectedIds={selectedProcessIds}
                    setSelectedIds={setSelectedProcessIds}
                    processTypes={processTypes}
                    onTypeChange={toggleProcessType}
                    showJuri={false}
                    showParcelado={true}
                    hideCheckbox={true}
                  />

                  <Separator className="my-10 bg-gradient-to-r from-transparent via-slate-300 to-transparent h-px" />
                </>
              )}

              {cessaoProcesses.length > 0 && (
                <>
                  <Separator className="my-6 md:my-10 bg-gradient-to-r from-transparent via-slate-300 to-transparent h-px" />
                  <ProcessList
                    processes={cessaoProcesses}
                    selectedIds={[]}
                    setSelectedIds={() => {}}
                    processTypes={{}}
                    onTypeChange={() => {}}
                    showJuri={false}
                    showParcelado={true}
                    hideCheckbox={true}
                  />
                </>
              )}

              <UserFormSubmitSection
                filteredProcesses={filteredProcesses}
                userData={userData}
                selectedProcessIds={selectedProcessIds}
                setSelectedProcessIds={setSelectedProcessIds}
                processTypes={processTypes}
                toggleProcessType={toggleProcessType}
                isSubmitting={isSubmitting}
                handleFormSubmit={handleFormSubmit}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                onSubmitSuccess={onSubmitSuccess}
                setCessao={setCessao}
                submitCessao={submitCessao}
              />
            </>
          ) : (
            <div className="text-center text-slate-600 py-12">
              <p className="text-lg">
                Selecione um usuário no painel de admin para ver os detalhes.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
